import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Divider, Link, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import moment from 'moment';

import { CreateApiTokenOutput, useCreateApiTokenMutation, useGetApiTokensQuery } from 'api';
import { Loaders } from 'components';
import { useCurrentUser, useNotify } from 'hooks';

import DeveloperLogin from './DeveloperLogin';

import NewKey from './NewKey';
import ExistingKey from './ExistingKey';
import theme from 'theme';

const ApiIndex: FC = () => {
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const [createdTokens, setCreatedTokens] = useState<CreateApiTokenOutput[]>([]);

  const { data, isLoading } = useGetApiTokensQuery(
    {},
    {
      onError: notify.queryError,
    }
  );

  const { mutate: createMutation, isLoading: isCreateLoading } = useCreateApiTokenMutation({
    onError: notify.mutationError,
    onSuccess: () => notify.info({ message: t('components:tokenCreated') }),
    onSettled: ({ createAPIToken }) => {
      setCreatedTokens([createAPIToken, ...createdTokens]);
    },
  });

  function handleCreateToken() {
    const tokenName = `${currentUser.full_name} - ${moment().format('lll')}`;
    createMutation({ data: { token_name: tokenName } });
  }

  function renderCreateToken() {
    return (
      <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid xs>
          <Grid container spacing={2} alignItems="center">
            <Grid>
              <VpnKeyIcon color="primary" />
            </Grid>
            <Grid>
              <Typography variant="subtitle2">{t('components:apiKey')}</Typography>
              <Typography>{t('components:apiKeyHelp')}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Button
            disabled={isCreateLoading}
            onClick={handleCreateToken}
            size="large"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.light,
              },
            }}
          >
            {isCreateLoading ? (
              <Loaders.Standard color="white" height={26} />
            ) : (
              <Typography color="primary">{t('components:newApiKey')}</Typography>
            )}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid xs={3}>
        <Typography variant="h6">{t('common:api')}</Typography>
      </Grid>
      <Grid xs>
        <Box marginBottom={3}>
          <Typography paragraph>
            <Trans key="components:apiKeysDeveloperIntro">
              The EveryoneSocial API allows you to access and manage your EveryoneSocial instance
              data. API Keys are used to manage your workspace data and you cannot perform actions
              on behalf of users. You can read more in our{' '}
              <Link
                color="primary"
                href="https://developers.everyonesocial.com"
                target="_blank"
                underline="hover"
              >
                developer documentation
              </Link>
              .
            </Trans>
          </Typography>
          <DeveloperLogin />
        </Box>
        {renderCreateToken()}
        {createdTokens.map(newToken => (
          <Box key={newToken.token_id} marginTop={4} marginBottom={3}>
            <Box marginBottom={4}>
              <Divider />
            </Box>
            <NewKey token={newToken} />
          </Box>
        ))}
        {isLoading && <Loaders.Standard />}
        {data?.getAPITokens.map(existingToken => (
          <Box key={existingToken.token_id} marginTop={4} marginBottom={3}>
            <Box marginBottom={4}>
              <Divider />
            </Box>
            <ExistingKey token={existingToken} />
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};

export default ApiIndex;
